import React from 'react'
import ExteriorImage from '@anewgo/exterior-image/dist'
import PropTypes from 'prop-types'
import { makeStyles, Typography } from '@material-ui/core'
import {
  createElevation,
  getCustomColorsForSelectedScheme,
  getCustomSchemeFromSelection,
  groupMaterialsByColorForNonCart,
} from '../../../../utils/helper'
import usePrintPageStyle from '../../../../hooks/usePrintPageStyle'
import BrochureMaterialList, {
  getBrochureDescriptionPoints,
} from './BrochureMaterialList'
import { gcsURL } from '../../../../utils/gcsUrl'
import { SHERWIN_WILLIAMS_LOGO } from '../../../constants/AppImages'
import {
  BROCHURE_INSET_IMAGE_WIDTH,
  BROCHURE_MAIN_NO_SCHEME_WIDTH,
} from '../brochureImageOptions'

const PAGE_WIDTH = 756 // at 72 dpi 8 1/2"
const PAGE_HEIGHT = 996

const styles = makeStyles({
  portraitPage: {
    width: `${PAGE_WIDTH}px`,
    height: `${PAGE_HEIGHT}px`,
  },
  landscapePage: {
    width: `${PAGE_HEIGHT}px`,
    height: `${PAGE_WIDTH}px`,
  },
  page: {
    display: 'flex',
    flexDirection: 'column',
    pageBreakAfter: 'always',
    backgroundColor: 'white',
    overflow: 'hidden',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'auto auto 1fr auto',
    alignItems: 'center',
    columnGap: '8px',
  },
  headerLeft: {
    gridColumn: '1',
  },
  headerRight: {
    gridColumn: '4',
    justifySelf: 'end',
  },
  projectName: {
    fontSize: '14px',
    fontWeight: 'bold',
    alignSelf: 'start',
  },
  headerCenter: {
    gridColumn: '2',
    // alignSelf: 'center',
  },
  buildingName: {
    display: 'inline',
    fontSize: '16px',
    fontWeight: 'bold',
    borderRight: '1px solid black',
    paddingRight: '8px',
  },
  schemeName: {
    display: 'inline',
    fontSize: '12px',
    paddingLeft: '8px',
  },
  repInfo: {
    gridColumn: '3',
    fontSize: '10px',
    lineHeight: 1.4,
    justifySelf: 'right',
  },
  coverPageContent: {
    flex: '1',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  exteriorImg: {
    width: '100%',
    flex: '0 1 auto', // size to the image, don't reserve a fixed band
    maxHeight: '65%', // never taller than today's reserved height (tall images unchanged)
    minHeight: '45%', // floor so dense pages can't shrink the image to nothing
    minWidth: 0,
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
    marginTop: '16px',
    overflow: 'hidden',
  },
  bottomSection: {
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    columnGap: '10px',
    paddingTop: '16px',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  materialListWrap: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  insetContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  insetLabel: {
    fontSize: '8px',
    marginBottom: '2px',
  },
  insetPhoto: {
    border: 'solid 3px white',
    borderRadius: '5px',
  },
  materialIndexes: {
    display: 'flex',
    flexWrap: 'wrap',
    margin: '3px 2px',
  },
  disclaimer: {
    marginTop: 'auto',
    fontSize: '9px',
    lineHeight: 1.1,
  },
  dateRow: {
    textAlign: 'right',
    fontSize: '8px',
    padding: '0',
  },
})

const SHWLogo = () => (
  <img
    alt="LOGO"
    src={gcsURL('app/cv/shw/app_images', SHERWIN_WILLIAMS_LOGO)}
    height={50}
    width={200}
    style={{ marginLeft: '1em', objectFit: 'contain' }}
  />
)

const DefaultPage = ({ property, selection, isPortrait, building, index }) => {
  const classes = styles()
  const { schemes } = property
  const selectedScheme = getCustomSchemeFromSelection(
    building,
    property,
    selection
  )
  const customColorsForSelectedScheme = getCustomColorsForSelectedScheme(
    building,
    property,
    selection,
    selectedScheme
  )

  usePrintPageStyle(isPortrait)

  const renderContentPageHeader = () => {
    const hasRep = property && property.rep
    const noRepStyle = !hasRep
      ? { gridTemplateColumns: '1fr auto 1fr' }
      : undefined

    return (
      <div className={classes.header} style={noRepStyle}>
        <div className={classes.headerLeft}>
          <div className={classes.projectName}>{property.name}</div>
        </div>
        <div
          className={classes.headerCenter}
          style={!hasRep ? { justifySelf: 'center' } : undefined}
        >
          <span className={classes.buildingName}>{building.name}</span>
          {selectedScheme !== undefined ? (
            <span className={classes.schemeName}>{selectedScheme.name}</span>
          ) : (
            <span className={classes.schemeName}>No Scheme Selected</span>
          )}
        </div>
        {hasRep ? (
          <div className={classes.repInfo}>
            <div>SW Rep: {property.rep.full_name}</div>
            <div>c: {property.rep.phone}</div>
            <div>e: {property.rep.email}</div>
          </div>
        ) : null}
        <div
          className={classes.headerRight}
          style={!hasRep ? { gridColumn: '3' } : undefined}
        >
          <SHWLogo />
        </div>
      </div>
    )
  }

  const getBrochureMaterials = () => {
    const { colorMaterialIdsMap: groupedMaterials, materials } =
      groupMaterialsByColorForNonCart(
        building,
        selectedScheme,
        customColorsForSelectedScheme
      )
    const descriptionPoints = getBrochureDescriptionPoints(groupedMaterials)

    return {
      groupedMaterials,
      descriptionPoints:
        descriptionPoints.length > 0
          ? descriptionPoints
          : materials.map((item, pointIndex) => ({
              ...item,
              pointNumber: pointIndex + 1,
            })),
    }
  }

  const renderSchemeFooter = () => {
    if (selectedScheme !== undefined) {
      const { groupedMaterials } = getBrochureMaterials()

      return <BrochureMaterialList groupedMaterials={groupedMaterials} />
    }
    return null
  }

  const renderSchemeDisclaimer = () => (
    <Typography variant="caption">
      Actual color may vary from on-screen representation. To confirm your color
      choices prior to purchase, please view a physical color chip, color card,
      or painted sample. Sherwin-Williams is not responsible for the content and
      photos shared by users of their color selection tools. The Cover The Earth
      logo and the Sherwin-Williams logo are trademarks owned or licensed by The
      Sherwin-Williams Company. {`©${new Date().getFullYear()} `} The
      Sherwin-Williams Company. {`${property.name}`}
    </Typography>
  )

  const hemiRenderedSummaryPage = () => {
    const { account } = property
    const { views } = building
    const elevation = createElevation(schemes, views[0])
    const thumbnailImage = views[0].thumbnail_src
    const directoryName = account.cloud_folder_name

    const insetImageUrl = gcsURL(
      `app/${directoryName}/images`,
      thumbnailImage,
      {
        w: BROCHURE_INSET_IMAGE_WIDTH,
        o: 'avif',
      }
    )
    const noSchemeMainImageUrl = gcsURL(
      `app/${directoryName}/images`,
      building.views[0].base_image_src,
      {
        w: BROCHURE_MAIN_NO_SCHEME_WIDTH,
        o: 'avif',
      }
    )

    const img = new Image()
    img.src = insetImageUrl
    img.onload = () => {
      document.getElementById(`insetImage${index}`).style.height = `${
        `${img.naturalHeight}` / 5
      }px`
      document.getElementById(`insetImage${index}`).style.maxHeight = '150px'
      document.getElementById(`insetImage${index}`).style.minHeight = '90px'
    }

    const { descriptionPoints } = selectedScheme
      ? getBrochureMaterials()
      : { descriptionPoints: [] }

    return selectedScheme ? (
      <div className={classes.coverPageContent}>
        <div className={classes.exteriorImg}>
          <ExteriorImage
            engineUrl={`${process.env.HEMI_SERVER_PREFIX}/api/cv`}
            client={account.account_name}
            maxWidth={1800}
            rootStyle={{ height: '100%', width: '100%' }}
            community={property.name}
            plan={property.buildings[0].name}
            elevation={elevation}
            selectedScheme={selectedScheme}
            colorMtd="HYBRID"
            directoryName={directoryName}
            colorSelections={
              customColorsForSelectedScheme
                ? Object.assign(
                    {},
                    ...Object.entries(customColorsForSelectedScheme)
                      .filter((key) => !Number.isNaN(parseInt(key, 10)))
                      .map(([, value]) => ({
                        [value.materialElementId]: {
                          paletteId: value.materialPaletteId,
                          id: value.id,
                        },
                      }))
                  )
                : {}
            }
            descriptionPoints={descriptionPoints}
          />
        </div>
        <div className={classes.bottomSection}>
          <div className={classes.leftColumn}>
            <div className={classes.materialListWrap}>
              {renderSchemeFooter()}
            </div>
            <div className={classes.disclaimer}>{renderSchemeDisclaimer()}</div>
          </div>
          <div className={classes.insetContainer}>
            <span className={classes.insetLabel}>Original Image</span>
            <img
              id={`insetImage${index}`}
              className={classes.insetPhoto}
              alt=""
              src={insetImageUrl}
            />
          </div>
        </div>
      </div>
    ) : (
      <div className={classes.coverPageContent}>
        <div className={classes.exteriorImg}>
          <img
            style={{
              maxHeight: '100%',
              maxWidth: '100%',
              margin: 'auto',
            }}
            alt=""
            src={noSchemeMainImageUrl}
          />
        </div>
        <div className={classes.bottomSection}>
          <div className={classes.leftColumn}>
            <div className={classes.materialListWrap}>
              {renderSchemeFooter()}
            </div>
            <div className={classes.disclaimer}>{renderSchemeDisclaimer()}</div>
          </div>
          <div className={classes.insetContainer}>
            <span className={classes.insetLabel}>Original Image</span>
            <img
              id={`insetImage${index}`}
              className={classes.insetPhoto}
              alt=""
              src={insetImageUrl}
            />
          </div>
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div
        className={`${classes.page} ${
          isPortrait ? classes.portraitPage : classes.landscapePage
        }`}
        key={building.id}
      >
        {renderContentPageHeader()}
        {hemiRenderedSummaryPage()}
        <div className={classes.dateRow}>{new Date().toLocaleDateString()}</div>
      </div>
    </React.Fragment>
  )
}

DefaultPage.propTypes = {
  property: PropTypes.object.isRequired,
  selection: PropTypes.object.isRequired,
  building: PropTypes.object.isRequired,
  isPortrait: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
}

export default DefaultPage
